import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ButtonLoader, LoadingScreen } from "Components";
import useAuth from "Context/useAuth";
import { authService } from "Service/Axios";
import { SAVE_PROFILE_URL, SEARCH_INSTITUTION_URL } from "Constant/api";
import { useUserTypeList } from "Constant/apiHooks";
import { numericRegex } from "Utils/regex";
import { Validator } from "Utils/validator";
import { USER_TYPE } from "Constant/constants";
import { paths } from "Constant/paths";

export default function Onboarding() {
  const { t } = useTranslation();
  const { isOnboardingCompleted, updateUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [institutionList, setInstitutionList] = useState([]);

  const [formData, setFormData] = useState({
    purpose: "",
    userType: "",
    institution: "",
    degree: "",
    yearOfCompletion: "",
    location: "",
    resume: "",
  });
  const { purpose, userType, institution, degree, yearOfCompletion, location } =
    formData;

  const [errMsg, setErrMsg] = useState({
    purpose: "",
    institution: "",
    degree: "",
    yearOfCompletion: "",
  });

  const { userTypeList, loading: userTypeLoading } = useUserTypeList();

  // Listing
  const getInstitutionList = async (value) => {
    try {
      const payload = {
        searchtext: value,
      };
      const response = await authService(
        SEARCH_INSTITUTION_URL,
        "POST",
        payload
      );
      setInstitutionList(response);
    } catch {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    if (e.target.name === "purpose") {
      const selectedPurpose = JSON.parse(e.target.value);
      if (Validator.text(e.target.value)) {
        setFormData((prev) => ({
          ...prev,
          purpose: selectedPurpose.name,
          userType: selectedPurpose.type,
        }));
        setErrMsg((prev) => ({
          ...prev,
          purpose: "",
        }));
      }
    }

    if (e.target.name === "institution") {
      getInstitutionList(e.target.value);
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          institution: t("common.messages.error.common.institution.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          institution: "",
        }));
      }
    }

    if (e.target.name === "degree") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          degree: t("common.messages.error.common.degree.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          degree: "",
        }));
      }
    }

    if (e.target.name === "yearOfCompletion") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          yearOfCompletion: t(
            "common.messages.error.common.yearOfCompletion.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          yearOfCompletion: "",
        }));
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.target.name === "yearOfCompletion") {
      const isNumber = numericRegex.test(e.key);
      const isControl =
        e.key === "Backspace" ||
        e.key === "Tab" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Delete";
      if (!isNumber && !isControl) {
        e.preventDefault();
      }
    }
  };

  // On Select
  const handleSelectInsitution = (inst) => {
    setFormData((prev) => ({
      ...prev,
      institution: inst,
    }));
    setErrMsg((prev) => ({
      ...prev,
      institution: "",
    }));
    setInstitutionList([]);
  };

  // Validation
  const getValidation = () => {
    return new Promise((resolve) => {
      if (currentStep === 1) {
        if (Validator.text(purpose)) {
          resolve(true);
        } else {
          setErrMsg((prev) => ({
            ...prev,
            purpose: t("common.messages.error.common.purpose.blank"),
          }));
          resolve(false);
        }
      } else if (currentStep === 2) {
        let valid = true;
        if (!Validator.text(institution)) {
          setErrMsg((prev) => ({
            ...prev,
            institution: t("common.messages.error.common.institution.blank"),
          }));
          valid = false;
        }
        if (!Validator.text(degree)) {
          setErrMsg((prev) => ({
            ...prev,
            degree: t("common.messages.error.common.degree.blank"),
          }));
          valid = false;
        }
        if (!Validator.text(yearOfCompletion)) {
          setErrMsg((prev) => ({
            ...prev,
            yearOfCompletion: t(
              "common.messages.error.common.yearOfCompletion.blank"
            ),
          }));
          valid = false;
        }
        resolve(valid);
      }
    });
  };

  // Handle
  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleNext = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const onCompleteOnboarding = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          onboarding: true,
          currentOnboardingStep: "/",
          userType: purpose,
          type: userType,
          institution,
          degree,
          passoutyear: yearOfCompletion,
          location,
        };
        const response = await authService(SAVE_PROFILE_URL, "POST", payload);
        if (
          response?.success &&
          response?.message === "User account has been created"
        ) {
          setCurrentStep(3);
          setLoading(false);
          setTimeout(() => {
            updateUser(response);
            onRedirect();
          }, 1500);
        } else {
          setLoading(false);
          toast.error(
            response?.message ||
              t("common.messages.notification.failed.serverError")
          );
        }
      } catch {
        toast.error(t("common.messages.notification.failed.serverError"));
      }
    }
  };

  const onRedirect = async () => {
    const isFormAplication = localStorage.getItem("isFormAplication");
    if (isFormAplication) {
      navigate(isFormAplication);
      localStorage.removeItem("isFormAplication");
    } else {
      navigate(
        userType === USER_TYPE.Applicant
          ? paths.applicant.dashboard
          : paths.admin.dashboard
      );
    }
  };

  if (isOnboardingCompleted) {
    return <Navigate to={paths.root} />;
  }

  return (
    <main className="main">
      <div className="justify-content-center h-screen d-flex flex-column align-items-center p-lg-5">
        {/* Step 1 */}
        {currentStep === 1 && (
          <div className="row justify-content-center w-100">
            <div className="col-md-8 col-lg-6">
              <div className="mb-10">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <h3 className="text-xl-4xl text-2xl font-semibold">
                    Let's personalize your experience!
                  </h3>
                  <span className="text-end font-medium ms-auto">1/2</span>
                </div>
                <div className="progress mb-4 h-2">
                  <div
                    className="progress-bar bg-primary rounded-pill"
                    role="progressbar"
                    style={{ width: "50%" }}
                    aria-valuenow="50"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>

              <div className="max-w-500 mx-auto">
                <h4 className="text-lg-xl text-lg font-semibold">
                  What is your purpose of using PlutoEdu?
                </h4>
                <p className="text-sm mb-4">
                  We'll get you started with personalized profiles based on your
                  goal
                </p>

                <div className="d-flex flex-column row-gap-3">
                  {userTypeLoading ? (
                    <LoadingScreen />
                  ) : userTypeList && userTypeList.length > 0 ? (
                    userTypeList.map((userType, index) => (
                      <div
                        className="option-card text-sm position-relative"
                        key={index.toString()}
                      >
                        <input
                          type="radio"
                          className="position-absolute left-4"
                          name="purpose"
                          id="purpose"
                          value={JSON.stringify({
                            name: userType.name,
                            type: userType.type,
                          })}
                          checked={purpose === userType.name}
                          onChange={handleChange}
                        />
                        <label
                          className="d-block cursor-pointer pl-10 py-4 pr-5"
                          htmlFor="internship"
                        >
                          {userType.name}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="option-card text-sm position-relative">
                      <p className="text-muted">
                        No data found. Please contact the admin.
                      </p>
                    </div>
                  )}

                  {errMsg.purpose && <p className="error">{errMsg.purpose}</p>}

                  <div className="mt-4 text-center">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg px-10 font-medium"
                      onClick={handleNext}
                    >
                      {t("common.button.continue")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {currentStep === 2 && (
          <>
            <div className="row justify-content-center w-100">
              <div className="col-md-8 col-lg-6">
                <div className="mb-10">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <h3 className="text-xl-4xl text-2xl font-semibold">
                      Let's personalize your experience!
                    </h3>
                    <span className="text-end font-medium ms-auto">2/2</span>
                  </div>
                  <div className="progress mb-4 h-2">
                    <div
                      className="progress-bar bg-primary rounded-pill"
                      role="progressbar"
                      style={{ width: "100%" }}
                      aria-valuenow="100"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>

                <div className="mx-auto">
                  <div className="form-group mb-3 relative">
                    <label
                      className="form-label fw-semibold mb-1"
                      htmlFor="institution"
                    >
                      {t("auth.onboarding.institution")}{" "}
                      <span className="text-red-800">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg rounded-lg"
                      id="institution"
                      placeholder={t("common.placeHolder.searchInstitution")}
                      name="institution"
                      value={institution}
                      onChange={handleChange}
                    />
                    {errMsg.institution && (
                      <p className="error">{errMsg.institution}</p>
                    )}

                    {institutionList && institutionList.length > 0 && (
                      <ul className="institution-list z-10">
                        {institutionList.map((inst) => (
                          <li key={inst.gid}>
                            <a
                              onClick={() => handleSelectInsitution(inst.name)}
                            >
                              {inst.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label
                        htmlFor="degree"
                        className="form-label fw-semibold mb-1"
                      >
                        Degree <span className="text-red-800">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg rounded-lg"
                        id="degree"
                        placeholder="Your Degree"
                        name="degree"
                        value={degree}
                        onChange={handleChange}
                      />
                      {errMsg.degree && (
                        <p className="error">{errMsg.degree}</p>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label
                        htmlFor="yearOfCompletion"
                        className="form-label fw-semibold mb-1"
                      >
                        Year of Completion{" "}
                        <span className="text-red-800">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={4}
                        className="form-control form-control-lg rounded-lg"
                        id="location"
                        placeholder="Your Year of Completion"
                        name="yearOfCompletion"
                        value={yearOfCompletion}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                      />
                      {errMsg.yearOfCompletion && (
                        <p className="error">{errMsg.yearOfCompletion}</p>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="location"
                      className="form-label fw-semibold mb-1"
                    >
                      Where are you currently based?
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg rounded-lg"
                      id="location"
                      placeholder="Your Location"
                      name="location"
                      value={location}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="mt-4 text-center">
                    {loading ? (
                      <ButtonLoader />
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg px-10 font-medium"
                        onClick={onCompleteOnboarding}
                      >
                        {t("common.button.completeYourOnboarding")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-auto ms-auto">
              <button
                className="text-decoration-none d-flex align-items-center gap-2 font-medium text-grey-800 border-0 bg-transparent"
                onClick={handlePrev}
              >
                <i className="bx bx-arrow-back"></i> Back
              </button>
            </div>
          </>
        )}

        {/* Step 4 */}
        {currentStep === 3 && (
          <>
            <div className="row justify-content-center w-100 text-center">
              <div className="col-md-8 col-lg-6">
                <div className="success-icon text-primary-800 bg-primary-50 mb-8">
                  <i className="bx bx-check"></i>
                </div>

                <div className="mb-8">
                  <h4 className="text-lg-xl text-lg font-semibold mb-1">
                    Account created successfully!
                  </h4>
                  <p className="text-muted mb-4">
                    Welcome aboard! Start your success journey with PlutoEdu!
                  </p>
                </div>

                <button
                  className="btn btn-primary btn-lg px-10 font-medium"
                  onClick={onRedirect}
                >
                  Let's Start!
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
